import { catalogApi, type CatalogApi } from '../api/CatalogApi'
import {
  isValidDefinitionsCatalog,
  isValidVisualizationTypesCatalog,
} from '../service/catalogGuards'
import { useMetricCatalogStore, type MetricCatalogStore } from '../stores/metricCatalogStore'
import {
  useVisualizationTypeCatalogStore,
  type VisualizationTypeCatalogStore,
} from '../stores/visualizationTypeCatalogStore'

import {
  ensureCatalogEntry,
  readCachedCatalogBody,
  type CatalogBodyGuard,
  type CatalogDeps,
} from '@/shared/catalog'

/** Manifest entry name = URL segment = cache key (BE contract). Never a hardcoded body URL. */
const DEFINITIONS = 'definitions'
const VISUALIZATION_TYPES = 'visualization_types'

export interface PrimeReportCatalogsDeps {
  /** Legacy cold-miss transport. */
  api?: CatalogApi
  /** Versioned transport + storage seam (`shared/catalog`). */
  catalog?: CatalogDeps
}

let primePromise: Promise<void> | null = null
let primeGeneration = 0

/** Invalidate both the one-flight and every continuation owned by the previous auth session. */
export function resetReportCatalogPrime(): void {
  primeGeneration += 1
  primePromise = null
}

/** Backward-compatible name for current module-08 consumers. */
export const resetMetricCatalogPrime = resetReportCatalogPrime

type IsCurrentPrime = () => boolean

async function fallbackDefinitions(
  api: CatalogApi,
  store: MetricCatalogStore,
  isCurrent: IsCurrentPrime,
): Promise<void> {
  if (!isCurrent() || store.status === 'ready') return
  try {
    // No version accompanies the legacy body, so it stays session-only.
    const body = await api.fetchDefinitionsLegacy()
    if (!isValidDefinitionsCatalog(body)) throw new Error('invalid legacy definitions body')
    if (isCurrent()) store.setCatalog(body)
  } catch {
    if (isCurrent()) store.markDegraded()
  }
}

async function fallbackVisualizationTypes(
  api: CatalogApi,
  store: VisualizationTypeCatalogStore,
  isCurrent: IsCurrentPrime,
): Promise<void> {
  if (!isCurrent() || store.status === 'ready') return
  try {
    const body = await api.fetchVisualizationTypesLegacy()
    if (!isValidVisualizationTypesCatalog(body)) {
      throw new Error('invalid legacy visualization types body')
    }
    if (isCurrent()) store.setCatalog(body)
  } catch {
    // Consumers use the truthful local renderer registry while this catalog is degraded.
    if (isCurrent()) store.markDegraded()
  }
}

/** One catalog slice: the shared runtime decides freshness, the store owns the domain shape,
 *  and only a `/catalog/` miss reaches the legacy endpoint. */
async function primeSlice<T>(
  name: string,
  isValidBody: CatalogBodyGuard<T>,
  deps: PrimeReportCatalogsDeps,
  isCurrent: IsCurrentPrime,
  apply: (body: T) => void,
  fallback: () => Promise<void>,
): Promise<void> {
  const outcome = await ensureCatalogEntry(name, isValidBody, deps.catalog)
  if (!isCurrent()) return
  if (outcome.status === 'ready') {
    apply(outcome.body)
    return
  }
  await fallback()
}

/** Module-08 static-catalog boot: hydrate both cached slices synchronously, then let the shared
 *  runtime read one manifest and revalidate only what its fingerprint says may have moved.
 *  Failures degrade per slice. */
async function runReportCatalogPrime(
  deps: PrimeReportCatalogsDeps,
  generation: number,
): Promise<void> {
  const isCurrent = (): boolean => generation === primeGeneration
  const api = deps.api ?? catalogApi
  const storage = deps.catalog?.storage ?? localStorage
  const metrics = useMetricCatalogStore()
  const visualizations = useVisualizationTypeCatalogStore()

  const cachedDefinitions = readCachedCatalogBody(DEFINITIONS, isValidDefinitionsCatalog, storage)
  const cachedVisualizations = readCachedCatalogBody(
    VISUALIZATION_TYPES,
    isValidVisualizationTypesCatalog,
    storage,
  )
  if (isCurrent() && cachedDefinitions) metrics.setCatalog(cachedDefinitions)
  if (isCurrent() && cachedVisualizations) visualizations.setCatalog(cachedVisualizations)
  if (isCurrent()) visualizations.beginPrime()

  await Promise.all([
    primeSlice(
      DEFINITIONS,
      isValidDefinitionsCatalog,
      deps,
      isCurrent,
      (body) => metrics.setCatalog(body),
      () => fallbackDefinitions(api, metrics, isCurrent),
    ),
    primeSlice(
      VISUALIZATION_TYPES,
      isValidVisualizationTypesCatalog,
      deps,
      isCurrent,
      (body) => visualizations.setCatalog(body),
      () => fallbackVisualizationTypes(api, visualizations, isCurrent),
    ),
  ])
  if (isCurrent()) visualizations.finishPrime()
}

export function primeReportCatalogs(deps: PrimeReportCatalogsDeps = {}): Promise<void> {
  primePromise ??= runReportCatalogPrime(deps, primeGeneration)
  return primePromise
}

/** Kept while existing callers migrate to the broader, accurate name. */
export const primeMetricCatalog = primeReportCatalogs
